import { useEffect, useMemo, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { AsyncOperation } from '../api/@types';
import contentsClient from '../client/medq-contents';
import { SubjectInfo } from '../services/contents/medq-contents.client';

export interface SubjectNode {
  _id: string;
  name: string;
  icon: string;
  resumeUrl: string;
  children?: SubjectNode[];
}

function find(node: SubjectNode, id: string): SubjectNode | undefined {
  if (node._id === id) {
    return node;
  }
  if (node.children) {
    // eslint-disable-next-line no-restricted-syntax
    for (const child of node.children) {
      const item = find(child, id);
      if (item) {
        return item;
      }
    }
  }
  return undefined;
}

function ancestors(id: string, tree?: SubjectNode[]): SubjectNode[] {
  if (tree) {
    // eslint-disable-next-line no-restricted-syntax
    for (const node of tree) {
      if (node._id === id) {
        return [node];
      }
      const f = ancestors(id, node.children);

      if (f.length > 0) {
        return [node, ...f];
      }
    }
  }

  return [];
}

function findByName(node: SubjectNode, id: string): SubjectNode | undefined {
  if (node.name === id) {
    return node;
  }
  if (node.children) {
    // eslint-disable-next-line no-restricted-syntax
    for (const child of node.children) {
      const item = findByName(child, id);
      if (item) {
        return item;
      }
    }
  }
  return undefined;
}

interface SubjectNodesOutput {
  subjectNodes: SubjectNode[];
}

const SUBJECT_NODES = gql`
  fragment SubjectData on SubjectNodeGql {
    _id,
    icon,
    name,
    resumeUrl,
  }
  {
    subjectNodes {
      name
      icon
      _id: externalId,
      resumeUrl
      children {
        ...SubjectData
        children {
          ...SubjectData
          children {
            ...SubjectData,
            children {
              ...SubjectData
            }
          }
        }
      }
    }
  }
`;

export type ListAllSubjectsFromContentsOutput = SubjectInfo[];

export function useListAllSubjectsFromContents(): AsyncOperation<ListAllSubjectsFromContentsOutput> {
  const [result, setResult] = useState<AsyncOperation<ListAllSubjectsFromContentsOutput>>({
    isLoading: true,
    data: undefined,
  });
  useEffect(() => {
    contentsClient.listAllSubjects().then((subjects) => {
      setResult({
        isLoading: false,
        data: subjects,
      });
    });
  }, []);
  return result;
}

export interface SubjectDAO {
  data: SubjectNode[];
  loading: boolean;
  getList: () => SubjectNode[];
  getSubjectNode: (_id: string) => SubjectNode | undefined;
  getChildrenList: (areaId: string) => SubjectNode[];
  isChild: (father: string, child: string) => boolean;
  getSubjectNodeByName: (_id: string) => SubjectNode | undefined;
  getAncestors: (id?: string) => SubjectNode[];
  getSubjectInfoFromContents: (id: string) => SubjectInfo | undefined;
}

export default function useSubjects(skip?: boolean): SubjectDAO {
  const subjectsFromContents = useListAllSubjectsFromContents();

  const { loading, data } = useQuery<SubjectNodesOutput>(SUBJECT_NODES, {
    fetchPolicy: 'cache-first',
    skip,
  });
  const areas = useMemo(() => data?.subjectNodes || [], [data]);

  const subjectsFromContentsMap = useMemo(() => (subjectsFromContents.data ?? []).reduce((acc, it) => {
    acc[it.subjectId] = it;
    return acc;
  }, {} as Record<string, SubjectInfo>), [subjectsFromContents]);

  return useMemo(() => {
    const getChildrenList = (_id: string): SubjectNode[] => {
      if (loading || !areas || subjectsFromContents.isLoading) {
        return [];
      }
      let father;
      // eslint-disable-next-line no-restricted-syntax
      for (const area of areas) {
        father = find(area, _id);
        if (father) {
          break;
        }
      }

      if (!father) {
        return [];
      }
      const copy = [...father.children || []];
      return copy.sort((a, b) => a.name.localeCompare(b.name));
    };

    const getSubjectNode = (_id: string): SubjectNode | undefined => {
      if (loading || !areas) {
        return undefined;
      }
      let father;
      // eslint-disable-next-line no-restricted-syntax
      for (const area of areas) {
        father = find(area, _id);
        if (father) {
          break;
        }
      }

      return father;
    };

    const getSubjectNodeByName = (_id: string): SubjectNode | undefined => {
      if (loading || !areas) {
        return undefined;
      }
      let father;
      // eslint-disable-next-line no-restricted-syntax
      for (const area of areas) {
        father = findByName(area, _id);
        if (father) {
          break;
        }
      }

      return father;
    };

    const getList = (): SubjectNode[] => {
      if (loading || !areas) {
        return [];
      }
      const copy = [...areas];
      return copy.sort((a, b) => a.name.localeCompare(b.name));
    };

    const isChild = (child: string, father?: string): boolean => {
      if (loading || !areas) {
        return false;
      }
      const children = father ? getChildrenList(father) : areas;
      return Boolean(children.find((item) => find(item, child)));
    };

    const getAncestors = (id?: string): SubjectNode[] => {
      if (!id) {
        return [];
      }
      return ancestors(id, getList());
    };

    const getSubjectInfoFromContents = (id: string): SubjectInfo | undefined => {
      return subjectsFromContentsMap[id];
    };

    return {
      data: areas,
      loading,
      getList,
      isChild,
      getChildrenList,
      getSubjectNode,
      getSubjectNodeByName,
      getAncestors,
      getSubjectInfoFromContents,
    };
  }, [areas, loading, subjectsFromContents.isLoading, subjectsFromContentsMap]);
}
